class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// Simple function to check if cart should show empty state
function shouldShowCartEmptyState(cart) {
  if (!cart || !cart.items || cart.item_count === 0) {
    return true;
  }
  
  // Check if all items are shipping protection products
  for (const item of cart.items) {
    const isProtection = item.vendor === 'SimplyInsurance' || 
                        item.handle === 'shipping-protection-4' ||
                        (item.product && item.product.tags && 
                         (item.product.tags.includes('simplyinsurance') || 
                          item.product.tags.some(tag => tag.toLowerCase() === 'simplyinsurance')));
    if (!isProtection) {
      return false; // Found a non-protection item
    }
  }
  
  return true; // All items are protection items
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          
          // Sync empty state after drawer update
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              const shouldShowEmpty = shouldShowCartEmptyState(cart);
              const cartDrawerWrapper = document.querySelector('cart-drawer');
              const cartDrawerItems = document.querySelector('cart-drawer-items');
              
              if (cartDrawerWrapper) {
                cartDrawerWrapper.classList.toggle('is-empty', shouldShowEmpty);
                const drawerHeader = cartDrawerWrapper.querySelector('.drawer__header');
                const drawerFooter = cartDrawerWrapper.querySelector('.drawer__footer');
                const drawerInnerEmpty = cartDrawerWrapper.querySelector('.drawer__inner-empty');
                
                if (drawerHeader) drawerHeader.style.display = shouldShowEmpty ? 'none' : '';
                if (drawerFooter) drawerFooter.style.display = shouldShowEmpty ? 'none' : '';
                if (drawerInnerEmpty) drawerInnerEmpty.style.display = shouldShowEmpty ? '' : 'none';
              }
              if (cartDrawerItems) {
                cartDrawerItems.classList.toggle('is-empty', shouldShowEmpty);
              }
            })
            .catch(() => {});
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Check if cart should show empty state (empty or only shipping protection)
        const shouldShowEmpty = shouldShowCartEmptyState(parsedState);
        this.classList.toggle('is-empty', shouldShowEmpty);
        
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        const cartDrawerItems = document.querySelector('cart-drawer-items');

        if (cartFooter) cartFooter.classList.toggle('is-empty', shouldShowEmpty);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', shouldShowEmpty);
        if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', shouldShowEmpty);
        
        // Hide/show drawer header and footer when empty, and show empty state content
        if (cartDrawerWrapper) {
          const drawerHeader = cartDrawerWrapper.querySelector('.drawer__header');
          const drawerFooter = cartDrawerWrapper.querySelector('.drawer__footer');
          const drawerInnerEmpty = cartDrawerWrapper.querySelector('.drawer__inner-empty');
          
          if (drawerHeader) drawerHeader.style.display = shouldShowEmpty ? 'none' : '';
          if (drawerFooter) drawerFooter.style.display = shouldShowEmpty ? 'none' : '';
          
          // Show/hide empty state content when cart is empty
          if (drawerInnerEmpty) {
            drawerInnerEmpty.style.display = shouldShowEmpty ? '' : 'none';
          }
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        // Re-query quantityElement and items after DOM replacement
        const quantityElementAfterRender =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const itemsAfterRender = document.querySelectorAll('.cart-item');
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (itemsAfterRender.length === parsedState.items.length && quantityElementAfterRender && updatedValue !== parseInt(quantityElementAfterRender.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (shouldShowCartEmptyState(parsedState) && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (itemsAfterRender.length > 0 && cartDrawerWrapper) {
          const firstCartItem = document.querySelector('.cart-item');
          if (firstCartItem) {
            trapFocus(cartDrawerWrapper, firstCartItem.querySelector('.cart-item__name'));
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
